/**
 * i18n Validators & Parsers — Frontend
 *
 * Single Source of Truth für DACH-spezifische Validierung und Parsing.
 *
 * Konvention:
 *   - Validator-Funktionen geben { valid: bool, error?: string } zurück.
 *   - Format-Funktionen geben einen formatierten String zurück.
 *   - Parser-Funktionen geben einen normalisierten Wert (Number/string ohne Formatierung) zurück.
 *
 * Verwendung in Komponenten:
 *   import { validateIBAN, formatIBAN, parseCurrency } from '@/utils/i18nValidators';
 */

// ────────── Constants ──────────

const COUNTRY = ['DE', 'AT', 'CH'];

const POSTCODE_RULES = {
  DE: { length: 5, pattern: /^\d{5}$/, hint: '5-stellig (z.B. 10115)' },
  AT: { length: 4, pattern: /^\d{4}$/, hint: '4-stellig (z.B. 1010)' },
  CH: { length: 4, pattern: /^\d{4}$/, hint: '4-stellig (z.B. 8001)' },
};

const IBAN_RULES = {
  DE: { length: 22, prefix: 'DE', hint: 'DE + 20 Stellen (insgesamt 22)' },
  AT: { length: 20, prefix: 'AT', hint: 'AT + 18 Stellen (insgesamt 20)' },
  CH: { length: 21, prefix: 'CH', hint: 'CH + 19 Stellen (insgesamt 21)' },
};

const VAT_RULES = {
  DE: {
    pattern: /^DE\d{9}$/i,
    placeholder: 'DE123456789',
    hint: 'DE + 9 Ziffern',
    normalize: (s) => s.toUpperCase().replace(/\s/g, ''),
  },
  AT: {
    pattern: /^ATU\d{8}$/i,
    placeholder: 'ATU12345678',
    hint: 'ATU + 8 Ziffern',
    normalize: (s) => s.toUpperCase().replace(/\s/g, ''),
  },
  CH: {
    // CHE-123.456.789 MWST oder CHE123456789MWST
    pattern: /^CHE-?\d{3}\.?\d{3}\.?\d{3}( MWST)?$/i,
    placeholder: 'CHE-123.456.789 MWST',
    hint: 'CHE-XXX.XXX.XXX MWST',
    normalize: (s) => {
      const clean = s.toUpperCase().replace(/\s/g, '');
      // CHE123456789 → CHE-123.456.789
      const m = clean.match(/^CHE-?(\d{3})\.?(\d{3})\.?(\d{3})(MWST)?$/);
      if (!m) return s.toUpperCase();
      return `CHE-${m[1]}.${m[2]}.${m[3]}${m[4] ? ' MWST' : ''}`;
    },
  },
};

const PHONE_PREFIX = { DE: '+49', AT: '+43', CH: '+41' };

const LOCALE_MAP = { DE: 'de-DE', AT: 'de-AT', CH: 'de-CH' };
const CURRENCY_MAP = { DE: 'EUR', AT: 'EUR', CH: 'CHF' };

// ────────── PLZ ──────────

export function validatePostcode(value, country = 'DE') {
  if (!value) return { valid: false, error: 'Postleitzahl ist erforderlich.' };
  const rule = POSTCODE_RULES[country] || POSTCODE_RULES.DE;
  if (!rule.pattern.test(String(value).trim())) {
    return { valid: false, error: `Ungültige PLZ — ${rule.hint}.` };
  }
  return { valid: true };
}

export function getPostcodeHint(country = 'DE') {
  return (POSTCODE_RULES[country] || POSTCODE_RULES.DE).hint;
}

// ────────── IBAN ──────────

/**
 * IBAN-Validierung mit ISO-13616 mod-97-Checksumme.
 * Akzeptiert Eingabe mit/ohne Spaces.
 */
export function validateIBAN(value, country = null) {
  if (!value) return { valid: false, error: 'IBAN ist erforderlich.' };
  const clean = String(value).replace(/\s/g, '').toUpperCase();

  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(clean)) {
    return { valid: false, error: 'IBAN ungültiges Format.' };
  }

  // Country-spezifische Längenprüfung wenn angegeben
  if (country) {
    const rule = IBAN_RULES[country];
    if (rule && (clean.length !== rule.length || !clean.startsWith(rule.prefix))) {
      return { valid: false, error: `${country}-IBAN: ${rule.hint}.` };
    }
  }

  // mod-97 Checksum
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (c) => (c.charCodeAt(0) - 55).toString());

  // BigInt für sehr lange IBAN-Zahlen
  let remainder = 0;
  for (const ch of numeric) {
    remainder = (remainder * 10 + parseInt(ch, 10)) % 97;
  }
  if (remainder !== 1) {
    return { valid: false, error: 'IBAN-Prüfziffer stimmt nicht.' };
  }
  return { valid: true };
}

/**
 * Formatiert IBAN in 4-er-Gruppen mit Spaces (z.B. "DE89 3704 0044 0532 0130 00").
 */
export function formatIBAN(value) {
  if (!value) return '';
  const clean = String(value).replace(/\s/g, '').toUpperCase();
  return clean.match(/.{1,4}/g)?.join(' ') || clean;
}

/** Normalisierung für Datenbank: keine Spaces, uppercase. */
export function normalizeIBAN(value) {
  return String(value || '').replace(/\s/g, '').toUpperCase();
}

export function getIBANHint(country = 'DE') {
  return (IBAN_RULES[country] || IBAN_RULES.DE).hint;
}

// ────────── USt-IdNr / VAT-ID ──────────

export function validateVatId(value, country = 'DE') {
  if (!value) return { valid: false, error: 'USt-IdNr ist erforderlich.' };
  const rule = VAT_RULES[country] || VAT_RULES.DE;
  const normalized = rule.normalize(value);
  if (!rule.pattern.test(normalized)) {
    return { valid: false, error: `Ungültiges Format — Beispiel: ${rule.placeholder}.` };
  }
  return { valid: true };
}

export function formatVatId(value, country = 'DE') {
  if (!value) return '';
  const rule = VAT_RULES[country] || VAT_RULES.DE;
  return rule.normalize(value);
}

export function getVatIdHint(country = 'DE') {
  return (VAT_RULES[country] || VAT_RULES.DE).hint;
}

export function getVatIdPlaceholder(country = 'DE') {
  return (VAT_RULES[country] || VAT_RULES.DE).placeholder;
}

// ────────── Telefon ──────────

/**
 * Sehr einfache Phone-Normalisierung:
 *  - Wenn ohne führendes "+" → Country-Prefix prepend (sofern erste 0 vorhanden, wird sie entfernt).
 *  - Whitespace, Bindestriche und Klammern werden entfernt für Storage.
 *  - Display-Formatierung mit Spaces.
 *
 * Hinweis: Für Production-Grade nutze libphonenumber-js. Wir starten hier minimal.
 */
export function normalizePhone(value, country = 'DE') {
  if (!value) return '';
  let v = String(value).replace(/[\s\-()]/g, '');
  if (v.startsWith('00')) v = '+' + v.slice(2);
  if (!v.startsWith('+')) {
    const prefix = PHONE_PREFIX[country] || '+49';
    v = v.replace(/^0/, '');
    v = prefix + v;
  }
  return v;
}

export function validatePhone(value, country = 'DE') {
  if (!value) return { valid: true }; // Phone optional
  const norm = normalizePhone(value, country);
  if (!/^\+\d{8,15}$/.test(norm)) {
    return { valid: false, error: 'Ungültige Telefonnummer.' };
  }
  return { valid: true };
}

export function formatPhone(value) {
  if (!value) return '';
  const norm = normalizePhone(value);
  // einfaches Spacing: +49 30 12345678 → "+49 30 12 34 56 78" macht zu viel Magic.
  // → wir gruppieren nur Country-Prefix + Rest.
  const m = norm.match(/^(\+\d{1,3})(\d+)$/);
  if (!m) return norm;
  return `${m[1]} ${m[2]}`;
}

export function getPhonePrefix(country = 'DE') {
  return PHONE_PREFIX[country] || '+49';
}

// ────────── Currency ──────────

/**
 * Parsen einer Currency-Eingabe in Number — locale-aware.
 *  DE/AT: "1.234,56" → 1234.56  · "1234,56" → 1234.56
 *  CH:    "1'234.56" → 1234.56  · "1234.56" → 1234.56
 * Akzeptiert auch leeren String → null.
 */
export function parseCurrency(input, country = 'DE') {
  if (input === null || input === undefined || input === '') return null;
  let s = String(input).trim();
  // Whitespace und Currency-Symbole entfernen
  s = s.replace(/[\s€]/g, '').replace(/CHF/gi, '');

  if (country === 'CH') {
    // Apostroph als 1000er, Punkt als Decimal
    s = s.replace(/'/g, '');
  } else {
    // DE/AT: Punkt als 1000er, Komma als Decimal → tausch
    // 1.234,56 → 1234.56
    s = s.replace(/\./g, '').replace(',', '.');
  }
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

/**
 * Format Number → Currency-String (locale-aware).
 *  formatCurrency(1234.56, 'DE') → "1.234,56 €"
 *  formatCurrency(1234.56, 'CH') → "CHF 1'234.55" (Schweiz: 5er-Rappenrundung optional)
 */
export function formatCurrency(amount, country = 'DE', options = {}) {
  const locale = LOCALE_MAP[country] || 'de-DE';
  const currency = CURRENCY_MAP[country] || 'EUR';
  const value = typeof amount === 'number' ? amount : parseFloat(amount);
  if (isNaN(value)) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...options,
  }).format(value);
}

export function getCurrencySymbol(country = 'DE') {
  return country === 'CH' ? 'CHF' : '€';
}

// ────────── Datum ──────────

/** Parsen eines DACH-Datums: "12.03.2026" → "2026-03-12" (ISO). */
export function parseDateDACH(input) {
  if (!input) return null;
  const m = String(input).trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) {
    // Vielleicht schon ISO?
    if (/^\d{4}-\d{2}-\d{2}/.test(input)) return input;
    return null;
  }
  const [, d, mo, y] = m;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** "2026-03-12" → "12.03.2026" für Anzeige in Inputs. */
export function formatDateDACH(isoOrDate) {
  if (!isoOrDate) return '';
  const date = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (isNaN(date.getTime())) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
}

// ────────── Country-Helpers ──────────

export const ALL_COUNTRIES = COUNTRY;

export function isValidCountry(code) {
  return COUNTRY.includes(String(code || '').toUpperCase());
}

export function getCountryLabel(code) {
  const map = { DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz' };
  return map[code] || code;
}

export function getCountryFlag(code) {
  const map = { DE: '🇩🇪', AT: '🇦🇹', CH: '🇨🇭' };
  return map[code] || '';
}
